import useAnimatedPress from '@ui/animated/useAnimatedPress'
import BlurEffect from '@ui/effects/BlurEffect'
import Border from '@ui/effects/Border'
import Gradient from '@ui/effects/Gradient'
import useRWD from '@ui/theme/useRWD'
import { Intensity } from '@ca/common'
import { ColorConfig } from '@ca/common/types'
import { colors } from '@ui/colors'
import PricePackage from '@order/PricePackage'
import AttributeColor from '@order/steps/attributes/Color/Color'
import AttributeTinting from '@order/steps/attributes/Tinting/Tinting'
import { getCTA } from '@order/steps/helpers'
import { StepOption, TintingIntensity } from '@order/steps/types'
import Typography from '@ui/typography'
import React from 'react'
import { useTranslation } from 'react-i18next'
import { Platform, Pressable, View } from 'react-native'
import Animated, {
  interpolate,
  useAnimatedStyle,
} from 'react-native-reanimated'
import cn from '@ui/utils/cn'
import { hex } from '@ui/utils/hex'

type OptionTintingVariant = 'small' | 'large'

interface Props {
  variant?: OptionTintingVariant
  option: StepOption
  colorConfig: ColorConfig
}

const BLUR = 100

export default function OptionTinting({
  variant = 'large',
  colorConfig,
  option,
}: Props) {
  const { tiles } = useRWD()
  const { t } = useTranslation(['steps'])
  const { animation, onPressIn, onPressOut } = useAnimatedPress()
  const height = tiles.blurOption.height
  const intensity: TintingIntensity = Intensity.utils.isValid(option.value)
    ? option.value
    : 'full'

  const styleGradient = useAnimatedStyle(() => ({
    opacity: interpolate(animation.value, [0, 1], [1, 0.75]),
  }))

  const styleMain = useAnimatedStyle(() => ({
    transform: [{ scale: interpolate(animation.value, [0, 1], [1, 1.1]) }],
  }))

  const _cta = getCTA(option.cta)
  const priceText = _cta ? t(_cta) : undefined

  return (
    <Pressable
      onPress={option.onPress}
      onPressIn={onPressIn}
      onPressOut={onPressOut}
    >
      <View
        className="relative flex items-center justify-center overflow-hidden rounded-2xl"
        style={{
          width: '100%',
          height,
          backgroundColor: colors.gray[300],
        }}
      >
        <View className="absolute h-full w-full">
          <AttributeColor
            colorConfig={colorConfig}
            size={height}
            style={{
              position: 'absolute',
              left: -24,
              top: '33%',
              transform: [{ scaleX: 3 }],
            }}
          />
          {Platform.select({
            default: (
              <BlurEffect
                tint="regular"
                intensity={BLUR}
                className="left-0 h-full"
              />
            ),
            web: (
              <div
                className="absolute left-0 top-0 h-full w-full"
                style={{ backdropFilter: `blur(${BLUR}px)` }}
              />
            ),
          })}
          <Gradient
            colors={[hex(colors.gray[75], 0.66), colors.gray[75]]}
            start={[0, 1]}
            end={[0, 0]}
            style={styleGradient}
          />
          <Gradient
            colors={[colors.white0, colors.white]}
            start={[0, 0.5]}
            end={[1, 0.5]}
            style={styleGradient}
          />
          <Border />
        </View>
        <View className="flex h-full w-full flex-row items-center justify-between p-8 pr-16">
          <View className="flex h-full justify-between">
            <View className="gap-1">
              <Typography
                variant="LGB"
                className={cn(
                  'whitespace-break-spaces',
                  Platform.select({
                    default: 'tracking-[-1]',
                    web: 'tracking-[-0.025em]',
                  })
                )}
              >
                {t(`steps:color.items.${colorConfig.name}.title`)}{' '}
                {option.title}
              </Typography>
              {option.desc && (
                <Typography variant="MD" className="color-desc">
                  {option.desc}
                </Typography>
              )}
            </View>
            <View className="self-start">
              <PricePackage
                price={option.price}
                prefix={option.pricePrefix}
                colorOption="gray"
                color={colorConfig.front[0].color}
                text={priceText}
              />
            </View>
          </View>
          <Animated.View style={styleMain}>
            <AttributeTinting
              colorConfig={colorConfig}
              intensity={intensity}
              scale={1.25}
            />
          </Animated.View>
        </View>
      </View>
    </Pressable>
  )
}
